/**
 * Morse — „Kennenlernen“: bevor ein NEUES Zeichen geübt wird, steht es einmal in Ruhe da. (06.10.2026)
 *
 * Martin: „So ist es mir noch viel zu schwer.“ Vorher kam ein neues Zeichen ohne Vorstellung mitten in die Gruppen.
 * Jetzt zeigt die Karte das Zeichen groß, sein Muster als Bild und seine Silben; „Anhören“ spielt es zweimal —
 * erst langsam, dann im richtigen Tempo — und das Bild leuchtet mit. Geübt wird dann mit „Start“ direkt darüber
 * (ein eigener Knopf „Üben“ war derselbe Weg ein zweites Mal — Prüfbefund, weg). Auf Stufe 1 sind es zwei Zeichen
 * (K und M).
 *
 * Die Karte sitzt im Bereich „Bereit“ von Hören und Geben. Sie verschwindet, sobald auf der Stufe geübt wurde
 * (morse-hilfe.ts merkt es) oder der Stand zeigt, dass das Zeichen schon gehört ist.
 */
import { kochStufe } from "../../shared/morse-koch.js";
import { MORSE_TABELLE } from "../../shared/morse.js";
import { leuchte, musterBild, musterSprechen, silbenText } from "./morse-bild.js";
import { nochKennenlernen } from "./morse-hilfe.js";
import { merkwortElement } from "./morse-merkwort.js";
import { KENNENLERNEN } from "./morse-texte.js";
import { el, hoereMitMeldung, knopf, type MorseUmgebung } from "./morse-teile.js";

/** Zwischen dem langsamen und dem normalen Abspielen. */
const KENNEN_PAUSE_MS = 500;

let kennenNr = 0;

export class MorseKennenlernen {
  readonly wurzel: HTMLElement;
  private readonly titel: HTMLElement;
  private readonly stufeZeile: HTMLElement;
  private readonly text: HTMLElement;
  private readonly reihe: HTMLElement;
  private zeichen: string[] = [];
  /** Jeder Tipp auf „Anhören“ zählt hoch — ein späterer überholt den früheren (auch in der Pause dazwischen). */
  private lauf = 0;

  constructor(
    private readonly u: MorseUmgebung,
    /** Kam kein Ton: in Worten dorthin, wo man hinschaut. */
    private readonly melde: (text: string) => void,
    /** Für die Diagnosezeile: welcher Reiter (ohne Inhalt). */
    private readonly stelle: string,
  ) {
    this.wurzel = el("section", "morse-kennen");
    this.stufeZeile = el("p", "morse-feld-titel");
    this.titel = el("h3", "morse-kennen-titel");
    this.titel.id = `morse-kennen-titel-${++kennenNr}`;
    this.wurzel.setAttribute("aria-labelledby", this.titel.id);
    this.text = el("p", "morse-kennen-text");
    this.reihe = el("div", "morse-kennen-reihe");
    this.wurzel.append(this.stufeZeile, this.titel, this.text, this.reihe);
    this.wurzel.hidden = true;
  }

  /** Die Zeichen, die noch kennenzulernen sind (leer: die Karte ist weg). */
  get offen(): readonly string[] {
    return this.zeichen;
  }

  /** Nach dem Stand neu zeichnen: sichtbar, solange auf der Stufe etwas Neues noch nicht kennengelernt ist. */
  zeichne(): void {
    const s = this.u.stand();
    const neu = nochKennenlernen(s.stufe, s.zeichen);
    const gleich = neu.length === this.zeichen.length && neu.every((z, i) => z === this.zeichen[i]);
    this.wurzel.hidden = neu.length === 0;
    if (gleich && this.reihe.childElementCount) return;
    this.zeichen = neu;
    if (!neu.length) return;
    this.stufeZeile.textContent = KENNENLERNEN.stufe(kochStufe(s.stufe));
    this.titel.textContent = KENNENLERNEN.titel(neu);
    this.text.textContent = KENNENLERNEN.text(neu);
    this.reihe.textContent = "";
    for (const z of neu) this.reihe.appendChild(this.baueZeichen(z));
  }

  private baueZeichen(z: string): HTMLElement {
    const m = MORSE_TABELLE[z] ?? "";
    const k = el("div", "morse-kennen-zeichen");
    const name = el("span", "morse-kennen-name", z);
    name.setAttribute("aria-hidden", "true");
    const bild = musterBild(m, "l");
    bild.setAttribute("aria-label", `${z}: ${musterSprechen(m)}`);
    const anhoeren = knopf(KENNENLERNEN.anhoeren, "morse-knopf morse-knopf--klein", () => void this.anhoeren(z, bild, anhoeren));
    anhoeren.setAttribute("aria-label", `${z} anhören: erst langsam, dann im richtigen Tempo`);
    // Das Merkwort (15.10.2026): eine Eselsbrücke gleich beim ersten Hören — Silbe mit „o“ = Strich.
    const merk = merkwortElement(z, "morse-kennen-merk");
    k.append(name, bild, silbenText(m, "morse-kennen-silben"), ...(merk ? [merk] : []), anhoeren);
    return k;
  }

  /** Zweimal: langsam, kurze Pause, normal — das Bild leuchtet mit. Ein neuer Tipp oder Stopp bricht ab. */
  private async anhoeren(z: string, bild: HTMLElement, k: HTMLButtonElement): Promise<void> {
    const mein = ++this.lauf;
    const spiele = async (langsam: boolean): Promise<boolean> => {
      let halt: () => void = () => undefined;
      const e = await hoereMitMeldung(this.u, z, this.melde, `${this.stelle} kennenlernen`, langsam, (plan, inMs) => {
        halt = leuchte(bild, plan, inMs);
      });
      halt();
      return e === "fertig" && mein === this.lauf;
    };
    if (!(await spiele(true))) return;
    await new Promise((r) => setTimeout(r, KENNEN_PAUSE_MS));
    // Weg ist die Karte auch, wenn ihr REITER verborgen ist (Prüfbefund K2: in der Tabelle klang sonst das zweite K).
    if (mein !== this.lauf || !this.wurzel.isConnected || this.wurzel.closest("[hidden]")) return;
    if (await spiele(false)) k.textContent = KENNENLERNEN.nochmal;
  }
}
